import React from 'react';
import { Button } from 'antd';
import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';

import { summarizeRequirementChanges } from '../../utils/quoteRequirements';
import type { QuoteRequirementsDraft } from './useQuoteRequirementsDraft';

interface Props {
    draft: QuoteRequirementsDraft;
    onApply: () => void;
    secondaryLabel: string;
    onSecondary: () => void;
    /** Show the secondary action even without changes (a modal's Cancel). */
    alwaysShowSecondary?: boolean;
}

const statusText = ({ applying, uploading, error, changes }: QuoteRequirementsDraft) => {
    if (applying) {
        return (
            <span className="quote-requirements-apply-status">
                <LoadingOutlined /> Saving and recalculating prices…
            </span>
        );
    }
    if (error) {
        return (
            <span className="quote-requirements-apply-status is-error">
                <ExclamationCircleOutlined /> {error}
            </span>
        );
    }
    if (uploading) {
        return (
            <span className="quote-requirements-apply-status">
                <LoadingOutlined /> Uploading files…
            </span>
        );
    }
    if (!changes.count) return null;

    return (
        <>
            <span className="quote-requirements-apply-status is-pending">Unsaved changes</span>
            <span className="quote-requirements-apply-summary">
                {summarizeRequirementChanges(changes)}. Prices will be recalculated when you save.
            </span>
        </>
    );
};

export const RequirementsApplyBar = ({ draft, onApply, secondaryLabel, onSecondary, alwaysShowSecondary }: Props) => (
    <div className="quote-requirements-apply-bar" data-testid="quote-requirements-apply-bar">
        <div aria-live="polite" className="quote-requirements-apply-text" role="status">
            {statusText(draft)}
        </div>
        <div className="quote-requirements-apply-actions">
            {alwaysShowSecondary || draft.changes.count ? (
                <Button data-testid="quote-requirements-discard" disabled={draft.applying} onClick={onSecondary}>
                    {secondaryLabel}
                </Button>
            ) : null}
            <Button
                data-testid="quote-requirements-apply"
                disabled={!draft.changes.count || draft.uploading}
                loading={draft.applying}
                onClick={onApply}
                type="primary"
            >
                {draft.applying ? 'Saving…' : 'Save changes'}
            </Button>
        </div>
    </div>
);

export default RequirementsApplyBar;
